/*
  Rainbow Floral — Header behaviour (extracted asset)

  This file holds ALL of the rf-header section's JavaScript, moved out
  of the section's old inline javascript block so the browser can cache
  it across page loads. It is loaded from sections/rf-header.liquid via
  `<script src defer>`. `defer` guarantees the DOM is fully parsed
  before this executes — same timing the old section javascript bundle
  had (Shopify already shipped it as a deferred asset) — and the code
  only reads native APIs plus DOM hooks (data-rf-* attributes and the
  data-rf-wysiwyg-products JSON island), so it has no dependency on
  global.js / pubsub.js / constants.js load order.

  The original block contained no Liquid interpolation (Shopify does
  not render Liquid inside section javascript tags anyway), so nothing
  needed to be passed in via data attributes — this is a verbatim move
  of the two original IIFE bodies into named init functions, plus a
  `shopify:section:load` hook: the theme editor re-renders sections
  without reloading the page, and an external script does not re-run
  on its own, so we re-init when the editor swaps the header section
  back in. All comments are preserved from the original.
*/
(function () {
  function initHeaderCore() {
    var header = document.querySelector('[data-rf-header]');
    if (!header) return;

    // Scroll state — flips the header into the opaque white scheme
    // via the `is-scrolled` class (matches the CSS toggle).
    var onScroll = function () {
      if (window.scrollY > 20) header.classList.add('is-scrolled');
      else header.classList.remove('is-scrolled');
      // Record the scroll moment. Scrolling slides the sticky nav under a
      // stationary cursor, which fires a fresh `mouseenter` on a trigger
      // and would re-open the mega + dim overlay — reads as "the overlay
      // activates when I scroll." openMegaWithIntent ignores hover-opens
      // within SCROLL_SUPPRESS_MS of this timestamp. (Date.now is fine
      // here — this is page JS, not the resume-sensitive workflow runtime.)
      lastScrollTs = Date.now();
      // Close any open mega-menu (and its dim backdrop) the moment the
      // page scrolls — a dropdown left hanging mid-scroll, with the
      // overlay dimming the page, reads as a glitch. closeAllMegas is a
      // hoisted function declaration below, safe to call here.
      if (header.classList.contains('is-mega-open')) closeAllMegas();
    };
    // Suppress hover-driven mega opens for a short window after scrolling.
    var lastScrollTs = 0;
    var SCROLL_SUPPRESS_MS = 450;
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    /*
      HERO-UNDER-HEADER OVERLAY — McQueens-exact trick.
      On pages where the hero photo should extend BEHIND the menu bar
      (homepage + the delivery page), we set the header's
      `margin-bottom` to a negative value equal to its own height.
      This makes the next sibling (the hero section) pull UP and start
      at the same y position as the header, so the hero photo flows
      behind the (transparent) header. Sticky-positioned header stays
      pinned at top on scroll; hero scrolls behind it.

      McQueens uses this exact pattern: their <x-header> has
      `margin-bottom: -147.17px` (the header's measured height),
      verified via live inspection.

      Re-measure on resize and on header-size change (ResizeObserver
      catches font-load / responsive shifts the static measurement
      would miss).
    */
    function pullHeroUnderHeader() {
      var isOverlayPage =
        document.body.classList.contains('template-index');
      if (!isOverlayPage) {
        header.style.marginBottom = '';
        return;
      }
      var h = header.getBoundingClientRect().height;
      // Round to a whole pixel to avoid sub-pixel jitter on retina
      header.style.marginBottom = (-Math.round(h)) + 'px';
    }
    pullHeroUnderHeader();
    window.addEventListener('resize', pullHeroUnderHeader, { passive: true });
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(pullHeroUnderHeader);
      ro.observe(header);
    }

    /*
      SPA NAVIGATION HOOK — the header lives OUTSIDE #MainContent, so
      its JS runs only once and never re-executes on an SPA page swap.
      rf-spa-nav.liquid syncs document.body.className to the new page
      and then fires `rf:spa:navigated`. We re-run the scroll + overlay
      logic here so the negative margin-bottom (which makes the hero
      sit under the header on index/delivery only) is recomputed for
      the new template — otherwise the homepage's overlay margin would
      stay applied on sub-pages and overlap their first section.
    */
    window.addEventListener('rf:spa:navigated', function () {
      onScroll();
      pullHeroUnderHeader();
    });

    // Mobile menu — menu lives outside <header> now (see comment in
    // markup) so we query document, not header. The menu's `top` is
    // set dynamically each open so it always starts right below the
    // header's current bottom edge (accounting for the announcement
    // bar, scroll-state padding changes, etc.).
    var toggle = header.querySelector('[data-rf-menu-toggle]');
    var menu = document.querySelector('[data-rf-mobile-menu]');
    function positionMenu() {
      if (!menu) return;
      var rect = header.getBoundingClientRect();
      menu.style.top = Math.max(0, rect.bottom) + 'px';
    }
    function setMobileMenu(open) {
      menu.classList.toggle('is-open', open);
      // Keep the header's hamburger-to-X animation working — its
      // CSS selectors hang off `.rf-header--mobile-open` on the header.
      header.classList.toggle('rf-header--mobile-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      menu.setAttribute('aria-hidden', open ? 'false' : 'true');
      // Keep inert in sync so focusable <a> children can't be tab-reached when menu is closed
      if (open) { menu.removeAttribute('inert'); } else { menu.setAttribute('inert', ''); }
      document.documentElement.style.overflow = open ? 'hidden' : '';
    }
    if (toggle && menu) {
      // Position on load so it's right by the time the user first opens
      positionMenu();
      window.addEventListener('resize', positionMenu, { passive: true });
      // Re-measure when scroll state changes the header's padding
      window.addEventListener('scroll', positionMenu, { passive: true });

      toggle.addEventListener('click', function () {
        // Re-measure right before opening to catch any layout shifts
        // (e.g. hero collapsed)
        positionMenu();
        setMobileMenu(!menu.classList.contains('is-open'));
      });

      // Close the menu when a real navigation link inside it is tapped.
      // Accordion toggles are <button>s (no href), so they keep the menu
      // open to reveal their sub-links; only <a href> rows dismiss it.
      // Critical for same-page / anchor / tel: links that don't trigger a
      // full page load — without this the menu (and the <html> overflow
      // lock) would stay stuck open.
      menu.addEventListener('click', function (e) {
        var link = e.target.closest && e.target.closest('a[href]');
        if (link && menu.contains(link)) setMobileMenu(false);
      });

      // bfcache: tapping a link then pressing Back can restore the page
      // with the menu still open and <html> locked — reset on restore.
      window.addEventListener('pageshow', function (e) {
        if (e.persisted) setMobileMenu(false);
      });
    }

    // Live cart count — listen to Shopify's cart:updated event and the global pubsub pattern
    var counts = header.querySelectorAll('[data-rf-cart-count]');
    function updateCount(n) {
      counts.forEach(function (el) {
        el.textContent = n;
        el.setAttribute('data-rf-cart-count', n);
      });
    }
    // Simple AJAX refresh after form submit
    document.addEventListener('submit', function (e) {
      var f = e.target;
      if (f.action && f.action.indexOf('/cart/add') !== -1) {
        setTimeout(function () {
          fetch('/cart.js').then(function (r) { return r.json(); }).then(function (d) {
            if (typeof d.item_count === 'number') updateCount(d.item_count);
          });
        }, 400);
      }
    });

    /*
      MEGA-MENU — multi-panel hover/focus controller. Each trigger
      has data-rf-megamenu-trigger="<key>"; the matching panel has
      data-rf-megamenu="<key>". Hovering the trigger opens its panel
      and closes any others. Hovering the panel keeps it open.
      Pointer leaving the whole header closes the active panel.
    */
    var megaTriggers = header.querySelectorAll('[data-rf-megamenu-trigger]');
    var megaPanels = header.querySelectorAll('[data-rf-megamenu]');
    var activePanel = null;
    function clearMegaStampTimers(panel) {
      if (!panel || !panel.__rfWystagStampTimers) return;
      panel.__rfWystagStampTimers.forEach(function (timer) { window.clearTimeout(timer); });
      panel.__rfWystagStampTimers = [];
    }
    function playMegaStampSequence(panel) {
      if (!panel) return;
      clearMegaStampTimers(panel);
      var tags = panel.querySelectorAll('[data-rf-wystag-compact-stamp]');
      if (!tags.length) return;
      panel.__rfWystagStampTimers = [];
      tags.forEach(function (tag) { tag.classList.remove('is-stamped'); });
      tags.forEach(function (tag, index) {
        var timer = window.setTimeout(function () {
          // Force reflow so reopening the mega replays the stamp cleanly.
          void tag.offsetHeight;
          tag.classList.add('is-stamped');
        }, index * 260);
        panel.__rfWystagStampTimers.push(timer);
      });
    }

    function closeAllMegas() {
      // Clear any pending close timer — we're closing now, no need to wait.
      // (closeIntent is declared later; clearTimeout(undefined) is a no-op
      // so this is safe both during module-init and at runtime.)
      if (typeof closeIntent !== 'undefined') clearTimeout(closeIntent);
      megaPanels.forEach(function (p) {
        clearMegaStampTimers(p);
        p.classList.remove('is-active');
        p.setAttribute('aria-hidden', 'true');
        p.querySelectorAll('[data-rf-wystag-compact-stamp].is-stamped').forEach(function (tag) {
          tag.classList.remove('is-stamped');
        });
      });
      megaTriggers.forEach(function (t) {
        var a = t.querySelector('a');
        if (a) a.setAttribute('aria-expanded', 'false');
      });
      header.classList.remove('is-mega-open');
      activePanel = null;
    }

    function openMega(key) {
      // Opening a mega cancels any pending close — the user is engaging.
      if (typeof closeIntent !== 'undefined') clearTimeout(closeIntent);
      var nextActivePanel = null;
      megaPanels.forEach(function (p) {
        if (p.getAttribute('data-rf-megamenu') === key) nextActivePanel = p;
      });
      var panelChanged = activePanel !== nextActivePanel;
      megaPanels.forEach(function (p) {
        var match = p.getAttribute('data-rf-megamenu') === key;
        p.classList.toggle('is-active', match);
        p.setAttribute('aria-hidden', match ? 'false' : 'true');
        if (match) activePanel = p;
      });
      megaTriggers.forEach(function (t) {
        var match = t.getAttribute('data-rf-megamenu-trigger') === key;
        var a = t.querySelector('a');
        if (a) a.setAttribute('aria-expanded', match ? 'true' : 'false');
      });
      header.classList.add('is-mega-open');
      if (panelChanged && nextActivePanel) playMegaStampSequence(nextActivePanel);
    }

    /*
      Hover-intent — wait a short moment before opening to filter out
      accidental sweeps across the header. Without this, brushing past
      a nav trigger while moving toward the hero image fires the open,
      which is the bug the owner reported.
    */
    var hoverIntent = null;
    var closeIntent = null;
    function openMegaWithIntent(key) {
      // Ignore the synthetic mouseenter that fires when scrolling slides
      // the nav under a still cursor — otherwise the overlay re-activates
      // on scroll. Genuine hover (cursor actually moving over the trigger)
      // happens well after the scroll-suppress window.
      if (Date.now() - lastScrollTs < SCROLL_SUPPRESS_MS) return;
      clearTimeout(hoverIntent);
      hoverIntent = setTimeout(function () { openMega(key); }, 120);
    }
    function cancelOpenIntent() {
      clearTimeout(hoverIntent);
      hoverIntent = null;
    }
    /*
      CLOSE-INTENT — symmetric to open-intent. When the cursor leaves
      a trigger OR its panel, start a 150ms timer. If the cursor
      doesn't return to either (e.g. user moved to another nav item,
      to the logo, or out of the menu area), close. If it DOES
      return within 150ms (e.g. diagonal traversal from trigger to
      panel) the timer is cancelled and the mega stays open.

      Without this, the mega stayed open until the cursor left the
      ENTIRE header — including when the cursor was over the logo
      or a sibling nav link that has nothing to do with the open
      panel. This brings the close behavior in line with McQueens.
    */
    function scheduleClose() {
      clearTimeout(closeIntent);
      closeIntent = setTimeout(closeAllMegas, 150);
    }
    function cancelClose() {
      clearTimeout(closeIntent);
      closeIntent = null;
    }

    megaTriggers.forEach(function (trigger) {
      var key = trigger.getAttribute('data-rf-megamenu-trigger');
      var link = trigger.querySelector('a');
      var panel = header.querySelector('[data-rf-megamenu="' + key + '"]');
      if (!panel) return;

      // Pointer-driven open/close — hover-intent guards against
      // accidental opens on quick cursor sweeps. Cancelling the
      // pending open when the cursor exits the trigger before the
      // delay elapses means a fast swipe never opens anything.
      // Entering the trigger also cancels any pending close timer
      // (handles "go back to the trigger" case after a brief exit).
      trigger.addEventListener('mouseenter', function () {
        cancelClose();
        openMegaWithIntent(key);
      });
      trigger.addEventListener('mouseleave', function () {
        cancelOpenIntent();
        scheduleClose();
      });
      // Hovering the panel keeps the mega open immediately and
      // cancels any in-flight close. Leaving the panel starts the
      // 150ms close timer.
      panel.addEventListener('mouseenter', function () {
        cancelClose();
        openMega(key);
      });
      panel.addEventListener('mouseleave', scheduleClose);

      // Keyboard-driven open via focusin — no delay for keyboard,
      // it's already intentional input.
      trigger.addEventListener('focusin', function () { openMega(key); });
      panel.addEventListener('focusin', function () { openMega(key); });

      // Touch: tap toggles instead of following the link first time
      if (link) {
        link.addEventListener('click', function (e) {
          if (window.matchMedia && window.matchMedia('(hover: none)').matches) {
            var isOpen = panel.classList.contains('is-active');
            if (!isOpen) {
              e.preventDefault();
              openMega(key);
            }
          }
        });
      }

      /*
        HIGHLIGHT-SET SWAP — when the customer hovers/focuses a column
        on the left, activate the matching set of product cards on the
        right. Each column has `data-rf-highlight-key="{handle}"`; each
        set has `data-rf-highlight-id="{same-handle}"`. Mouse leaves
        the panel → restore the `.is-default` set (first column's).
        Result: hovering "Birthday" reveals birthday products; hovering
        "Bouquets" reveals bouquet products; etc.
      */
      /*
        Per-merchant preference: the featured panel should stay FIXED on
        the default ("What you see, what you get") collection instead of
        swapping as the cursor passes over each category column. The
        column hover/focus swap is intentionally disabled — the default
        set keeps its render-time `.is-active.is-default` and never moves.
        To restore per-column previews, re-add the mouseenter/focusin
        listeners that called setActiveHighlight(col's data-rf-highlight-key).
      */
    });

    /*
      Auto-close on link click inside a mega panel — without this,
      clicking a category in the dropdown left the panel visually
      open while the page navigated underneath it (the panel lives
      OUTSIDE #MainContent so SPA-nav never touches it). Delegated
      from the <header> so every link in every panel is covered,
      including links rendered later by JS (e.g. the wysiwyg
      highlight cards when the rotator swaps the pair).

      `closeAllMegas` synchronously strips the is-active classes;
      the link's default navigation still fires immediately after,
      so there's no race with the SPA-nav fetch.
    */
    header.addEventListener('click', function (e) {
      // Match BOTH links inside a panel AND the mega trigger link itself
      // (e.g. "Shop"). The trigger lives in [data-rf-megamenu-trigger],
      // OUTSIDE .rf-header__mega, so the old panel-only selector never
      // caught it — clicking "Shop" navigated via SPA-nav while the panel
      // stayed open over the new page.
      var link = e.target.closest('.rf-header__mega a, [data-rf-megamenu-trigger] a');
      if (!link) return;
      // On touch, the first tap OPENS (its own handler calls
      // preventDefault). Don't close in that case — only close when the
      // click actually navigates.
      if (e.defaultPrevented) return;
      closeAllMegas();
    });

    // Single close handler — covers mouseleave from header,
    // focusout, and Escape key.
    header.addEventListener('mouseleave', closeAllMegas);
    header.addEventListener('focusout', function () {
      setTimeout(function () {
        if (!header.contains(document.activeElement)) {
          closeAllMegas();
        }
      }, 0);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && header.classList.contains('is-mega-open')) {
        closeAllMegas();
      }
    });

    /*
      SEARCH OVERLAY — open on icon click; close on Escape, backdrop
      click, close-button click. Focuses the input on open so users
      can type immediately. Locks body scroll while open.
    */
    var searchToggle = header.querySelector('[data-rf-search-toggle]');
    var searchOverlay = document.querySelector('[data-rf-search-overlay]');
    if (searchToggle && searchOverlay) {
      var searchInput = searchOverlay.querySelector('[data-rf-search-input]');
      var searchClosers = searchOverlay.querySelectorAll('[data-rf-search-close]');

      var openSearch = function () {
        searchOverlay.classList.add('is-open');
        searchOverlay.removeAttribute('inert');
        searchOverlay.setAttribute('aria-hidden', 'false');
        document.documentElement.style.overflow = 'hidden';
        // Defer focus until after the slide-down animation starts
        // so the keyboard doesn't pop up before the panel is visible.
        setTimeout(function () { if (searchInput) searchInput.focus(); }, 50);
      };
      var closeSearch = function () {
        searchOverlay.classList.remove('is-open');
        searchOverlay.setAttribute('inert', '');
        searchOverlay.setAttribute('aria-hidden', 'true');
        document.documentElement.style.overflow = '';
        searchToggle.focus();
      };

      searchToggle.addEventListener('click', openSearch);
      searchClosers.forEach(function (el) {
        el.addEventListener('click', closeSearch);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && searchOverlay.classList.contains('is-open')) {
          closeSearch();
        }
      });
    }

    /*
      MOBILE ACCORDION (Shop expansion) — toggles the hidden attribute
      on the content div and flips aria-expanded on the button. Pure
      no-JS-height-animation pattern: simpler, no jank, plays nicely
      with screen readers (hidden = removed from the a11y tree).
    */
    var mobileAccordions = document.querySelectorAll('[data-rf-mobile-accordion]');
    mobileAccordions.forEach(function (li) {
      var btn = li.querySelector('.rf-header__mobile-accordion-toggle');
      var content = li.querySelector('.rf-header__mobile-accordion-content');
      if (!btn || !content) return;
      btn.addEventListener('click', function () {
        var isExpanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
        if (isExpanded) {
          content.setAttribute('hidden', '');
        } else {
          content.removeAttribute('hidden');
        }
      });
    });
  }

  /*
    Mega-menu WYSIWYG card refresh — every `data-rotate-ms` ms, the
    two visible product cards swap their content (image, title,
    price, href) to the next pair from the rotator's JSON data
    island. We update card attributes in place rather than swapping
    DOM nodes, so layout never shifts and the cards stay
    keyboard-focusable across refreshes.

    Pair advancement: pairIdx += 2 each tick, wrapping at data.length.
    With 8-12 products in the WYSIWYG collection, that's a 4-6 tick
    full cycle (10-15s) before any product repeats.

    Honors prefers-reduced-motion by skipping the interval entirely
    so cards stay on the initial pair.
  */
  function initMegaWysiwygRefresh() {
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var rotators = document.querySelectorAll('[data-rf-wysiwyg-rotator]');
    rotators.forEach(function (rotator) {
      var dataEl = rotator.querySelector('[data-rf-wysiwyg-products]');
      if (!dataEl) return;
      var products;
      try { products = JSON.parse(dataEl.textContent || '[]'); }
      catch (e) { return; }
      if (!products.length) return;

      var strip = rotator.querySelector('[data-rf-wysiwyg-strip]');
      if (!strip) return;
      var rotateMs = parseInt(rotator.getAttribute('data-rotate-ms'), 10) || 5000;

      function applyCard(card, product) {
        if (!card || !product) return;
        var img = card.querySelector('.rf-header__mega-highlight-img');
        var title = card.querySelector('.rf-header__mega-highlight-title');
        var price = card.querySelector('.rf-header__mega-highlight-eyebrow');
        card.setAttribute('href', product.url);
        if (img) {
          img.setAttribute('src', product.img);
          img.setAttribute('alt', product.title);
          img.removeAttribute('srcset');
        }
        if (title) title.textContent = product.title;
        if (price) price.textContent = product.price;
      }

      if (reduced) return;

      // nextIdx tracks the next product to scroll in (starts after the 3 initially rendered)
      var initialCount = strip.querySelectorAll('[data-rf-wysiwyg-card]').length;
      var nextIdx = initialCount % products.length;
      var isAnimating = false;

      setInterval(function () {
        if (isAnimating) return;
        var firstCard = strip.firstElementChild;
        if (!firstCard) return;

        // Measure the pixel distance to slide: one card width + the gap between cards.
        var cardW = firstCard.getBoundingClientRect().width;
        var gapPx = parseFloat(getComputedStyle(strip).columnGap) || parseFloat(getComputedStyle(strip).gap) || 12;
        var slideBy = cardW + gapPx;

        // Clone the first card's DOM structure for the incoming card.
        var incoming = firstCard.cloneNode(true);
        incoming.removeAttribute('data-rf-wysiwyg-card');
        incoming.classList.remove('is-refreshing');
        applyCard(incoming, products[nextIdx]);
        nextIdx = (nextIdx + 1) % products.length;

        // Preload the card after next so it's cached before it's needed.
        var preload = new Image();
        preload.src = products[nextIdx].img;

        strip.appendChild(incoming);
        isAnimating = true;

        // getBoundingClientRect above already forced a layout flush, so
        // transition:none (from the previous cleanup) is committed. Setting
        // transition + transform here triggers the animation correctly.
        strip.style.transition = 'transform 0.65s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        strip.style.transform = 'translateX(-' + slideBy + 'px)';

        var cleaned = false;
        function cleanup() {
          if (cleaned) return;
          cleaned = true;
          strip.style.transition = 'none';
          strip.style.transform = '';
          var leaving = strip.firstElementChild;
          if (leaving) strip.removeChild(leaving);
          isAnimating = false;
        }

        strip.addEventListener('transitionend', function onEnd(e) {
          // Only react to the strip's own transform transition, not child transitions.
          if (e.target !== strip || e.propertyName !== 'transform') return;
          strip.removeEventListener('transitionend', onEnd);
          cleanup();
        });
        // Fallback in case transitionend doesn't fire (hidden tab, reduced-motion OS toggle mid-tick, etc.)
        setTimeout(cleanup, 750);
      }, rotateMs);
    });
  }

  /*
    BOOT — run both init routines once on load. With `defer` the DOM
    is guaranteed parsed at this point, so every querySelector above
    finds the section's markup (the script tag itself lives inside
    the rf-header section).
  */
  function initRfHeader() {
    initHeaderCore();
    initMegaWysiwygRefresh();
  }
  initRfHeader();

  /*
    THEME EDITOR — `shopify:section:load` fires after the editor
    re-renders a section (the old DOM is replaced wholesale, taking
    the previously-bound element listeners with it). Re-running init
    re-binds everything to the fresh nodes. Note: document/window
    level listeners from the previous run remain attached but only
    reference detached nodes, so they're inert; the duplication is
    editor-only (the storefront never fires this event) and benign.
  */
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-rf-header]')) {
      initRfHeader();
    }
  });
})();
